import React, { useRef, useState, useCallback } from 'react'
import {
    Backdrop,
    Box,
    Button,
    Card,
    Chip, CircularProgress,
    Dialog,
    Divider, IconButton,
    Slider,
    Tooltip,
    Typography,
} from '@mui/material'
import { upperCase } from 'lodash'
import { t } from 'i18next'
import { Icon } from '@iconify/react'
import { useHorse } from '../../hooks/useHorse'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store'
import { TUser } from '../../shared/types/user.type'
import {
    setEditHorseHeightModal,
    setEditHorseWeightModal,
    setHorseToEdit,
} from '../../store/reducers/modal.reducer'
import Hierarchy from './Hierarchy'
import Cropper from 'react-easy-crop'
import { THorse } from '../../shared/types/horse.type'
import { getCroppedImg } from "../../shared/utils/imageUtils";
import { useFileUpload } from "../../hooks/useFileUpload";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import AvatarEditor from "../user/AvatarEditor";

type HorseInfoProps = {
    horse?: THorse
}

function HorseInfoCard({ horse }: HorseInfoProps) {
    const user = useSelector<RootState>((state) => state.user.user) as TUser
    const dispatch = useDispatch()
    const { getHorseWeight, getHorseHeight } = useHorse({ enableQuery: false })
    const { uploadImage, imageIsLoading, imageLoadingFailed } = useFileUpload()
    const { updateHorseMutation } = useHorse({ enableQuery: false })
    const getHorseBreed = () => {
        if (typeof horse?.breed !== 'string') {
            return t(
                horse?.breed?.ifce_code
                    ? `breed:${horse?.breed?.ifce_code}`
                    : 'unkown'
            )
        }
        return ''
    }

    const editHorseWeight = () => {
        dispatch(setEditHorseWeightModal(true))
        dispatch(setHorseToEdit(horse ?? null))
    }

    const editHorseHeight = () => {
        dispatch(setEditHorseHeightModal(true))
        dispatch(setHorseToEdit(horse ?? null))
    }

    // 🖼️ Cropper state
    const [imageSrc, setImageSrc] = useState<string | null>(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [cropDialogOpen, setCropDialogOpen] = useState(false)

    const queryClient = useQueryClient()
    const inputRef = useRef<HTMLInputElement | null>(null)


    const onFileChange = (file: File | null) => {
        if (file) {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                setImageSrc(reader.result as string)
                setCropDialogOpen(true)
                // Reset the input so selecting the same file again triggers onChange
                if (inputRef.current) {
                    inputRef.current.value = ''
                }
            }
        }
    }

    const onCropComplete = useCallback((_: any, croppedPixels: any) => {
        setCroppedAreaPixels(croppedPixels)
    }, [])


    const handleCropSave = async () => {
        if (!imageSrc || !croppedAreaPixels) return
        try {
            const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels)

            //Get file from blob
            const file = new File([croppedImage], 'cropped.jpg', { type: 'image/jpeg' })
            setCropDialogOpen(false)
            // You can preview or upload the croppedImage here

            if (!horse?.id) return

            let avatar_file = await uploadImage({ file, isPublic: true, category: 'HORSE_AVATAR' })

            await updateHorseMutation.mutateAsync({ id: horse.id, data: { avatar_file: avatar_file } })

            await queryClient.refetchQueries(['horse', horse.id])

            toast(t('addHorse:editSuccess'), { type: 'success' })

        } catch (error: any) {

            const messageError = error?.response?.data?.message || 'error:generic'

            console.error('Crop failed:', error)
            toast(t(messageError), { type: 'error' })

        }
    }

    return (
        <>


            <Card sx={{ p: 2, minHeight: '100%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" flexDirection="column" position="relative" width="100%">
                        <Box
                            display="flex"
                            flexDirection={{ md: 'row', xs: 'column' }}
                            alignItems="center"
                            gap={2}
                        >
                            <AvatarEditor avatar={horse?.avatar_url || ""} imageIsLoading={imageIsLoading} imageLoadingFailed={imageLoadingFailed} onImageChange={onFileChange}></AvatarEditor>




                            <Box
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                                flexWrap="wrap"
                                gap={1}
                            >
                                <Box
                                    display="flex"
                                    flex={1}
                                    justifyContent={{ md: 'start', xs: 'center' }}
                                    flexDirection="column"
                                >
                                    <Typography
                                        alignSelf={{ md: 'flex-start', xs: 'center' }}
                                        variant="subtitle2"
                                    >
                                        {upperCase(horse?.name)}
                                    </Typography>

                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        flexWrap="wrap"
                                        mt={1}
                                    >
                                        <Chip
                                            icon={<Icon icon="ph:gender-intersex-light" />}
                                            sx={{ borderRadius: 1, flex: 1 }}
                                            label={t(`genders:byKey:${horse?.gender ?? 'unknown'}`)}
                                        />
                                        <Chip
                                            icon={<Icon icon="carbon:parent-child" />}
                                            sx={{ borderRadius: 1, flex: 1 }}
                                            label={getHorseBreed()}
                                        />
                                        <Chip
                                            onClick={editHorseWeight}
                                            icon={<Icon icon="tabler:weight" />}
                                            sx={{ borderRadius: 1, flex: 1 }}
                                            label={
                                                horse?.weight
                                                    ? getHorseWeight(horse?.weight ?? 0, user.unit_system)
                                                    : t('horse:addWeight')
                                            }
                                        />
                                        <Chip
                                            onClick={editHorseHeight}
                                            icon={<Icon icon="circum:line-height" />}
                                            sx={{ borderRadius: 1, flex: 1 }}
                                            label={
                                                horse?.height
                                                    ? getHorseHeight(horse?.height ?? 0, user.unit_system)
                                                    : t('horse:addHeight')
                                            }

                                        />
                                        {
                                            horse?.rfid &&
                                            <Tooltip arrow title={t('metadataParameters:details:horseRfid')} placement='top'>
                                                <Chip
                                                    icon={<Icon icon="icon-park-outline:chip" />}
                                                    sx={{ borderRadius: 1, flex: 1 }}
                                                    label={horse.rfid}
                                                />
                                            </Tooltip>
                                        }

                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />
                        <Box
                            position="relative"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Box width="50%">
                                <Hierarchy horse={horse} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Card>

            {/*  Crop Dialog */}
            <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} maxWidth="sm" fullWidth>
                <Box position="relative" width="100%" height="400px">
                    <Cropper
                        image={imageSrc!}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </Box>
                <Box p={2}>
                    <Typography>Zoom</Typography>
                    <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, z) => setZoom(z as number)} />
                    <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
                        <Button onClick={() => setCropDialogOpen(false)}>{t('button:cancel')}</Button>
                        <Button variant="contained" onClick={handleCropSave}>{t('button:upload')}</Button>
                    </Box>
                </Box>
            </Dialog>
            {/* Loading backdrop */}
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={imageIsLoading || updateHorseMutation.isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}

export default HorseInfoCard
